import type {
  EnvironmentInjector,
  Injector,
  StaticProvider,
  ViewContainerRef,
} from '@angular/core';

import type { Observable } from 'rxjs';
import { of as observableOf } from 'rxjs';

export enum DialogWidth {
  FullWidth = '100%',
  FullSize = 'auto',
  LargeXXX = '1500px',
  LargeXX = '1224px',
  LargeX = '1080px',
  Large = '860px',
  Medium = '760px',
  Small = '640px',
  SmallX = '540px',
  SmallXX = '420px',
}

// Valid ARIA roles for a dialog element
export type DialogRole = 'dialog' | 'alertdialog';

export interface DialogConfigOptions {
  viewContainerRef?: ViewContainerRef | null;
  injector?: Injector;
  // The ARIA role of the dialog element
  role?: DialogRole;
  data?: {};
  // Whether the user can use escape or clicking outside to close a modal
  disableClose?: boolean;
  // If close isn't disabled, should we show the close button?
  showCloseButton?: boolean;
  width?: DialogWidth;
  maxWidth?: string;
  minWidth?: string;
  height?: string;
  backdropStyle?: 'cover' | 'default';
  coverBackdropStyle?: 'light' | 'neutral';
  disableCloseOnBackdrop?: boolean;
  containerOptions?: {
    hasPadding?: boolean;
    hasPaddingBottom?: boolean;
    verticallyCentered?: boolean;
    contentWithoutMargin?: boolean;
    fullscreenOnMobile?: boolean;
  };
  closeTooltip?: string;
  closeGuard?: () => Observable<boolean>;
  providers?: StaticProvider[];
  environmentInjector?: EnvironmentInjector;
}

export interface DialogConfig extends DialogConfigOptions {
  role: DialogRole;
  disableClose: boolean;
  showCloseButton: boolean;
  closeGuard: () => Observable<boolean>;
}

export function buildDialogConfig(options: DialogConfigOptions = {}): DialogConfig {
  return {
    role: <DialogRole>'dialog',
    disableClose: false,
    showCloseButton: true,
    closeGuard: () => observableOf(true),
    backdropStyle: 'default',
    closeTooltip: 'rtu.dialog.close_tooltip',
    ...options,
  };
}
